import React, { FC } from 'react';

import CurriculumArticleContentStrategy from '@core/domain/course/strategy/CurriculumArticleContentStrategy';
import CurriculumAssignmentContentStrategy from '@core/domain/course/strategy/CurriculumAssignmentContentStrategy';
import CurriculumExaminationContentStrategy from '@core/domain/course/strategy/CurriculumExaminationContentStrategy';
import CurriculumQuizContentStrategy from '@core/domain/course/strategy/CurriculumQuizContentStrategy';
import CurriculumSurveyContentStrategy from '@core/domain/course/strategy/CurriculumSurveyContentStrategy';
import CurriculumVideoContentStrategy from '@core/domain/course/strategy/CurriculumVideoContentStrategy';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

import CurriculumVideoPreview from './courseCurriculumVideoPreview';
import { CourseCurriculumContentStyle } from './style';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { convertDurationTime } from '@helpers/utils';
import useDialogWithoutUI from '@hooks/useDialog';
import { CourseCurriculumContentStrategy } from '@redux/course/type/courseDetailType';

type CourseCurriculumContentProps = {
  strategy: CourseCurriculumContentStrategy;
  courseIdName: string;
};

const CourseCurriculumContent: FC<CourseCurriculumContentProps> = ({ strategy, courseIdName }) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  const isPreview = strategy instanceof CurriculumVideoContentStrategy ? strategy.getIsPreview() : false;

  const displaySuffix = () => {
    switch (strategy.constructor) {
      case CurriculumArticleContentStrategy:
        return t('course_detail.course_curriculum.article');
      case CurriculumQuizContentStrategy:
        return t('course_detail.course_curriculum.quiz');
      case CurriculumAssignmentContentStrategy:
        return t('course_detail.course_curriculum.assignment');
      case CurriculumExaminationContentStrategy:
        return t('course_detail.course_curriculum.examination');
      case CurriculumSurveyContentStrategy:
        return t('course_detail.course_curriculum.survey');
      case CurriculumVideoContentStrategy:
        return convertDurationTime(strategy.getDuration()).simpleFormat.flexibleTime();
      default:
        return '';
    }
  };
  const videoPreviewDialog = useDialogWithoutUI({});
  const handleOpenPreview = () => {
    // TODO: waiting implement open video preview
    videoPreviewDialog.onOpen();
  };

  const handleClosePreview = () => {
    videoPreviewDialog.onClose();
  };

  return (
    <>
      <CourseCurriculumContentStyle
        sx={isPreview ? { cursor: 'pointer' } : undefined}
        onClick={() => (isPreview ? handleOpenPreview() : undefined)}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent={{ md: 'space-between' }}
          gap={1}
        >
          <Box display="flex" gap={1}>
            <Box minWidth={18} height={18} display="flex" justifyContent="center">
              <FontAwesomeIcon
                icon={strategy.getIcon()}
                style={{ fontSize: 16, marginTop: 4, color: theme.palette.textV2.disabled }}
              />
            </Box>
            <Box>
              {isPreview ? (
                <Typography
                  variant="body1lanna"
                  fontWeight={700}
                  color={theme.palette.primary.main}
                  sx={{ textDecoration: 'underline', textDecorationThickness: 1, overflowWrap: 'anywhere' }}
                >
                  {strategy.getName()}
                </Typography>
              ) : (
                <Typography variant="body1lanna" color={theme.palette.textV2.primary}>
                  {strategy.getName()}
                </Typography>
              )}
              {strategy.getAttachmentCount() > 0 ? (
                <Typography variant="body1lanna" color={theme.palette.textV2.primary} mt={0.5} component="p">
                  {t('course_detail.course_curriculum.has_attachment_download', {
                    file: strategy.getAttachmentCount(),
                  })}
                </Typography>
              ) : null}
            </Box>
          </Box>

          {isPreview ? (
            <Box ml={{ xs: '20px', md: 0 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{ borderRadius: 100, height: 24, fontSize: 16, fontWeight: 700 }}
                endIcon={<FontAwesomeIcon icon="fa-light fa-circle-play" style={{ fontSize: 11 }} />}
                onClick={handleOpenPreview}
              >
                <Typography variant="h9lanna" fontWeight={700} whiteSpace="nowrap">
                  {t('course_detail.course_curriculum.free_preview')}
                </Typography>
              </Button>
            </Box>
          ) : null}
        </Box>

        <Box textAlign="end">
          <Typography variant="body1lanna" color={theme.palette.textV2.primary}>
            {displaySuffix()}
          </Typography>
        </Box>
      </CourseCurriculumContentStyle>
      {videoPreviewDialog.isOpened && (
        <CurriculumVideoPreview
          open={videoPreviewDialog.isOpened}
          onClose={handleClosePreview}
          chapterId={strategy.getId()}
          chapterName={strategy.getName()}
          courseIdName={courseIdName}
        />
      )}
    </>
  );
};

export default CourseCurriculumContent;
